import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CoachService, coachDay, shouldAutoOpen } from 'src/app/services/coach/coach.service';
import { CoachActivityService, chatKey, conversationKey } from 'src/app/services/coach/coach-activity.service';
import { CoachMoment } from 'src/app/services/coach/coach.models';
import { SupportChatbotService } from 'src/app/components/support-chatbot/support-chatbot.service';

/** How often the auto-open rule is re-checked while a stuck moment waits. */
const AUTO_OPEN_TICK_MS = 1500;
/** The preview bubble hides itself after this long; the glow stays. */
const PREVIEW_VISIBLE_MS = 15000;
/** `?help=recap` (the weekly recap email's link) waits this long for the recap moment to load. */
const RECAP_LINK_WAIT_MS = 20000;

/** True when the address carries the recap email's `help=recap`. Pure — unit tested. */
export function isRecapLink(search: string): boolean {
  return new URLSearchParams(search).get('help') === 'recap';
}

/**
 * The Help Coach's presence outside the Help window (contract §6): the Help
 * button's glow, the one-line preview bubble beside it, and the auto-open rule.
 * Lives in the app shell (tiny, no markdown) so the glow shows before the Help
 * panel's chunk is ever loaded.
 */
@Component({
  selector: 'app-coach-launcher',
  standalone: true,
  imports: [TranslateModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host { display: contents; }
    .coach-preview {
      position: fixed;
      z-index: 9000;
      left: var(--coach-preview-left, 1rem);
      bottom: var(--coach-preview-bottom, 1rem);
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      max-width: min(22rem, calc(100vw - 2rem));
      padding: 0.625rem 0.5rem 0.625rem 0.875rem;
      border-radius: 0.75rem;
      background: var(--surface-primary, var(--background-color));
      color: var(--text-color);
      border: 1px solid color-mix(in srgb, var(--accent-color, #ff5322) 45%, transparent);
      box-shadow: 0 10px 28px rgba(0, 0, 0, 0.22);
      font-size: 0.8125rem;
      line-height: 1.4;
    }
    .coach-preview-open {
      flex: 1;
      min-width: 0;
      padding: 0;
      border: none;
      background: none;
      color: inherit;
      font: inherit;
      text-align: start;
      cursor: pointer;
    }
    .coach-preview-open:focus-visible, .coach-preview-close:focus-visible { outline: 2px solid var(--accent-color); outline-offset: 2px; }
    .coach-preview-close {
      flex: none;
      width: 1.5rem;
      height: 1.5rem;
      border: none;
      border-radius: 0.375rem;
      background: none;
      color: var(--text-secondary);
      font-size: 1rem;
      line-height: 1;
      cursor: pointer;
    }
  `],
  template: `
    @if (previewMoment(); as moment) {
      <div class="coach-preview" role="status" data-testid="coach-preview">
        <button type="button" class="coach-preview-open" (click)="openMoment(moment)">💡 {{ moment.preview }}</button>
        <button type="button" class="coach-preview-close" [attr.aria-label]="'help.coach.preview.hide' | translate" (click)="hidePreview(moment)">×</button>
      </div>
    }
  `,
})
export class CoachLauncherComponent implements OnInit {
  private readonly coach = inject(CoachService);
  private readonly activity = inject(CoachActivityService);
  private readonly support = inject(SupportChatbotService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  /** Captured at boot, before routing can rewrite the address. */
  private readonly recapLinkPending = signal(typeof location !== 'undefined' && isRecapLink(location.search));

  /** Moments whose bubble the person closed (×) — the moment itself stays in Help. */
  private readonly hiddenPreviews = signal<ReadonlySet<string>>(new Set());
  private readonly expiredPreviews = signal<ReadonlySet<string>>(new Set());
  private readonly helpOpen = signal(false);

  readonly glowing = computed(() => !!this.coach.activeMoment() && !this.coach.optedOut());

  readonly previewMoment = computed(() => {
    const moment = this.coach.activeMoment();
    if (!moment || this.coach.optedOut() || this.helpOpen()) return null;
    if (this.hiddenPreviews().has(moment.id) || this.expiredPreviews().has(moment.id)) return null;
    return moment;
  });

  constructor() {
    effect(() => {
      const on = this.glowing();
      if (typeof document !== 'undefined') document.documentElement.classList.toggle('coach-has-tip', on);
    });
    // The recap email's link opens Help on the newest recap moment, then drops the param.
    effect(() => {
      if (!this.recapLinkPending()) return;
      const recap = this.coach.moments().find((m) => m.kind === 'recap');
      if (recap) this.resolveRecapLink(recap);
    });
    effect(() => {
      const moment = this.previewMoment();
      if (!moment) return;
      this.placeBesideHelpButton();
      const timer = setTimeout(() => this.expiredPreviews.update((s) => new Set(s).add(moment.id)), PREVIEW_VISIBLE_MS);
      return () => clearTimeout(timer);
    });
  }

  ngOnInit(): void {
    this.coach.start();
    if (this.recapLinkPending()) {
      const timer = setTimeout(() => this.resolveRecapLink(null), RECAP_LINK_WAIT_MS);
      this.destroyRef.onDestroy(() => clearTimeout(timer));
    }
    const sub = this.support.open$.subscribe((open) => this.helpOpen.set(open));
    const timer = setInterval(() => this.checkAutoOpen(), AUTO_OPEN_TICK_MS);
    this.destroyRef.onDestroy(() => {
      sub.unsubscribe();
      clearInterval(timer);
      if (typeof document !== 'undefined') document.documentElement.classList.remove('coach-has-tip');
    });
  }

  openMoment(moment: CoachMoment): void {
    this.coach.focus(moment);
    void this.coach.markShown(moment);
    this.support.openPanel();
  }

  private resolveRecapLink(recap: CoachMoment | null): void {
    if (!this.recapLinkPending()) return;
    this.recapLinkPending.set(false);
    if (recap) {
      this.openMoment(recap);
    } else {
      this.support.openPanel();
    }
    void this.router.navigate([], { queryParams: { help: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  hidePreview(moment: CoachMoment): void {
    this.hiddenPreviews.update((s) => new Set(s).add(moment.id));
  }

  /** The auto-open rule, evaluated against the live screen. */
  checkAutoOpen(now = new Date()): void {
    const moment = this.coach.activeMoment();
    if (!moment?.autoOpen || this.coach.optedOut()) return;
    const context = this.activity.currentContext();
    const currentKey = context.chatId ? chatKey(context.chatId) : context.conversationId ? conversationKey(context.conversationId) : null;
    const surface = currentKey ? this.activity.surface(currentKey) : undefined;
    const open = shouldAutoOpen({
      moment,
      today: coachDay(now),
      lastAutoOpenDay: this.coach.lastAutoOpenDay(),
      currentSurfaceKey: currentKey,
      surfaceBusy: surface ? surface.isBusy() : false,
      typing: this.activity.isTyping(now.getTime()),
      helpOpen: this.helpOpen(),
    });
    if (!open) return;
    this.coach.recordAutoOpen(coachDay(now));
    this.openMoment(moment);
  }

  /** Sit the bubble next to the kit sidebar's Help button when it is on screen. */
  private placeBesideHelpButton(): void {
    if (typeof document === 'undefined') return;
    const buttons = Array.from(document.querySelectorAll<HTMLElement>('.crwd-sidenav__help-btn'));
    const button = buttons.find((b) => b.getBoundingClientRect().width > 0);
    const root = document.documentElement;
    if (!button) {
      root.style.removeProperty('--coach-preview-left');
      root.style.removeProperty('--coach-preview-bottom');
      return;
    }
    const rect = button.getBoundingClientRect();
    root.style.setProperty('--coach-preview-left', `${Math.round(rect.right + 8)}px`);
    root.style.setProperty('--coach-preview-bottom', `${Math.max(8, Math.round(window.innerHeight - rect.bottom))}px`);
  }
}
